import React from 'react';
import { BasicPageWrapper, GivelifyLabel, GivelifyLink } from '@givelify/ui';
import { pushEvent } from '@givelify/utils';
import { Grid, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ShareIcon from '../../../assets/icon/share.svg';
import CopyGivingLink from '../../../components/CopyGivingLink';
import Config from '../../../config';
import { useConnectorState, useDonee, useSocialMediaGivingURL } from '../../../provider/Connector';
import {
    TW_GIVING_LOADED,
    TW_GIVING_SHARE,
} from '../../../utils/cleverTapEvents';
import { useTranslations } from '../../../utils/translations';
import { Preview } from './Preview';
import {
    Breadcrumbs,
    Container,
    Divider,
    ShareButton,
    TipContainer,
} from './style';
import { useTwitterShareURL } from './useTwitterShareURL';

export const TwitterGiving: React.FCC = () => {
    const donee = useDonee();
    const socialMediaGivingURL = useSocialMediaGivingURL();
    const { onlineGivingURLs, routePaths } = useConnectorState();
    const navigate = useNavigate();

    const { pageTitle, increaseGiving, copyOrgLink } = useTranslations(
        'socialMediaGiving.twitter.page',
        {
            pageTitle: 'title',
            increaseGiving: ['title', 'desc', 'shareOnTwitter'],
            copyOrgLink: ['title', 'desc'],
        },
    );

    const givingLink =
        onlineGivingURLs.find((x) => x.channel === 'TWT')?.shortLink || '';

    const twitterShareURL = useTwitterShareURL(donee.name, givingLink);

    const handleShareClick = React.useCallback(() => {
        window.open(twitterShareURL, '_blank');
        pushEvent(TW_GIVING_SHARE);
    }, [twitterShareURL]);

    React.useEffect(() => {
        pushEvent(TW_GIVING_LOADED);
    }, []);

    return (
        <BasicPageWrapper showDivider contentWrapper="simple" title={pageTitle}>
            <Breadcrumbs
                active="social-root"
                hideBadge={true}
                options={[
                    {
                        id: 'social-root',
                        label: 'Social media giving',
                        onClick: () =>
                            routePaths.socialMediaGiving &&
                            navigate(socialMediaGivingURL),
                    },
                    {
                        id: 'twitter',
                        label: 'Twitter',
                    },
                ]}
            />
            <Container data-testid="twitter-giving">
                <Grid container spacing={5}>
                    <Grid item mobile={12} smallTablet={6} width="100%">
                        <GivelifyLabel
                            text={increaseGiving.title}
                            variant="body1B"
                        />
                        <GivelifyLabel
                            marginBottom={2}
                            marginTop={1}
                            text={increaseGiving.desc}
                            variant="body1"
                        />
                        <ShareButton
                            data-testid="share-on-twitter-button"
                            name="shareOnTwitter"
                            onClick={handleShareClick}
                            startIcon={
                                <img
                                    alt="share"
                                    src={ShareIcon}
                                    title="share"
                                />
                            }
                            text={increaseGiving.shareOnTwitter}
                            variant="primary"
                        />
                        <Divider text="or" />
                        <GivelifyLabel
                            text={copyOrgLink.title}
                            variant="body1B"
                        />
                        <GivelifyLabel
                            marginBottom={2}
                            marginTop={1}
                            text={copyOrgLink.desc}
                            variant="body1"
                        />
                        <CopyGivingLink givingLink={givingLink} />
                    </Grid>
                    <Grid item mobile={12} smallTablet={6} width="100%">
                        <Preview />
                    </Grid>
                </Grid>
                <TipContainer
                    link="https://twitter.com/twittersupport/status/458707240325615617"
                    media="twitter"
                />
            </Container>
            <Stack marginBottom={6} marginTop={8} textAlign="center">
                <GivelifyLink
                    href={Config.supportUrl}
                    rel="noreferrer"
                    target="_blank"
                    text="Need help? Contact support"
                />
            </Stack>
        </BasicPageWrapper>
    );
};
